import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Switch, Route, useParams } from 'react-router-dom';
import { useCollect } from 'hooks';

import App from 'contexts/App';

import Tabs from 'components/Misc/Tabs';

import Information from 'containers/Shared/Webinars/Profile/Widgets/Information';
import Course from 'containers/Shared/Webinars/Profile/Widgets/Course';
import Teacher from 'containers/Shared/Webinars/Profile/Widgets/Teacher';

import Settings from 'containers/Shared/Webinars/Profile/Settings';
import Preview from 'containers/Shared/Webinars/Profile/Preview';
import Students from 'containers/Shared/Webinars/Profile/Students';

import { useWebinarProfileStore } from 'store/webinar/useWebinarProfileStore';

const Profile = () => {
	// Cache current object's id
	const { id } = useParams();

	// Use app's context
	const { setTitle } = useContext(App);

	// Store active profile tab
	const [tab, setTab] = useState(null);

	// Store profile tabs
	const tabs = useMemo(() => ([
		{ url: `/webinars/${id}`, text: 'Priekšskatījums', name: '' },
		{ url: `/webinars/students/${id}`, text: 'Studenti', name: 'students' },
	]), [id]);

	// Store request parameters
	const parameters = useMemo(() => ({
		eager: '[subject, course.teacher]'
	}), []);

	// Store requested webinar
	const [[result, progress], collect] = useCollect(`webinars/${id}`, parameters);

	// Use webinar's profile store
	const [webinar, onInit, onReset] = useWebinarProfileStore(state => [
		state.webinar,
		state.onInit,
		state.onReset
	]);

	// Set title on load
	useEffect(() => setTitle(webinar?.title), [setTitle, webinar]);

	// Collect webinar on mount and on parameter change
	useEffect(() => {
		(async () => await collect())();
	}, [collect]);

	// Initialize webinar's data after collection
	useEffect(() => {
		if (result) onInit(result);

		// Reset webinar's data on unmount
		return () => onReset();
	}, [result]);

	return (
		<div className="flex flex-col gap-4 xl:flex-row xl:items-start">
			<div className="grid grid-cols-1 gap-4 w-full sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-1 xl:max-w-xs">
				{/* Webinar's information */}
				<Information />

				{/* Webinar's related teacher */}
				<Teacher progress={progress} />
				
				{/* Webinar's related course */}
				<Course progress={progress} />
			</div>

			<div className="flex-1">
				{/* Profile tabs */}
				<Tabs
					tabs={tabs}
					selected={tab}
				/>

				<Switch>
					{/* Webinar's edit page */}
					<Route exact path="/webinars/edit/:id(\d+)">
						<Settings setTab={setTab} />
					</Route>

					{/* Webinar's preview */}
					<Route exact path="/webinars/:id(\d+)">
						<Preview tab="" setTab={setTab} />
					</Route>

					{/* Webinar's students */}
					<Route exact path="/webinars/students/:id(\d+)">
						<Students tab="students" setTab={setTab} />
					</Route>
				</Switch>
			</div>
		</div>
	);
};

export default Profile;
